/**
 * Pure reducers for the live-transcript document.
 *
 * Moved out of the client's `use-transcript-socket` hook so the document
 * mutation logic — and crucially its null-document seeding (MP-182/186) — can
 * be unit-tested without React, and reused by any viewer (web or mobile).
 *
 * A viewer normally seeds `document` from a `document_state` message. But the
 * server has a branch (writer present, Redis doc TTL-expired) that announces
 * `writer_connected` with NO document body, so the viewer's document stays
 * null. The `reduce*` entry points below seed a fresh empty document in that
 * case instead of dropping the incoming text — which was the "writer typing,
 * no text" bug on the Windows web viewer and the mobile WebView viewer.
 */
/** A fresh, empty transcript document. Used to seed before any state arrives. */
export function createEmptyDocument(id, title = '') {
    return { id, title, paragraphs: [], version: 0, lastModified: 0 };
}
/** Apply a single document operation, immutably. Unknown ops are no-ops. */
export function applyOperation(doc, op) {
    switch (op.op) {
        case 'insert':
            if (!op.newParagraph)
                return doc;
            if (op.position !== undefined) {
                return {
                    ...doc,
                    paragraphs: [
                        ...doc.paragraphs.slice(0, op.position),
                        op.newParagraph,
                        ...doc.paragraphs.slice(op.position),
                    ],
                };
            }
            return { ...doc, paragraphs: [...doc.paragraphs, op.newParagraph] };
        case 'delete':
            if (!op.paragraphId)
                return doc;
            return { ...doc, paragraphs: doc.paragraphs.filter((p) => p.id !== op.paragraphId) };
        case 'replace': {
            if (!op.paragraphId || !op.content)
                return doc;
            const targetId = op.paragraphId;
            const newSegments = op.content;
            return {
                ...doc,
                paragraphs: doc.paragraphs.map((p) => p.id === targetId ? { ...p, segments: newSegments } : p),
            };
        }
        case 'format':
            return doc;
        default:
            return doc;
    }
}
/** Apply a batched document update (operations + new version/timestamp). */
export function applyUpdate(doc, update) {
    const updated = update.operations.reduce((d, op) => applyOperation(d, op), doc);
    return { ...updated, version: update.version, lastModified: update.timestamp };
}
/** Apply a streamed-text frame: append a new paragraph or update it in place. */
export function applyStreamingText(doc, data) {
    const { paragraphId, text, isNewParagraph } = data;
    if (isNewParagraph && !doc.paragraphs.some((p) => p.id === paragraphId)) {
        return {
            ...doc,
            paragraphs: [
                ...doc.paragraphs,
                { id: paragraphId, segments: [{ text }], timestamp: Date.now() },
            ],
            lastModified: Date.now(),
        };
    }
    return {
        ...doc,
        paragraphs: doc.paragraphs.map((p) => p.id === paragraphId ? { ...p, segments: [{ text }] } : p),
        lastModified: Date.now(),
    };
}
/**
 * Decide whether an update applies to the document a viewer holds.
 *
 * - `apply`: it was diffed from exactly this version — or it names no base
 *   version (WebSocket-writer updates, older servers), which keeps the old
 *   unconditional behaviour and the null-document seeding (MP-182/186).
 * - `stale`: the viewer already holds this version or a newer one. Its
 *   initial snapshot can be read after a push whose broadcast is still in
 *   flight; applying that delta again would duplicate text.
 * - `gap`: the viewer holds an older version, or none, so it missed an
 *   update. Applying it would leave a wrong document (for a null document, a
 *   fragment of the lecture); the viewer must ask the server for a fresh
 *   snapshot for itself.
 *
 * MU N3c (7. 10. 2026): this is what lets the server stop re-seeding every
 * viewer with a full snapshot whenever one of them joins.
 */
export function classifyDocumentUpdate(doc, update) {
    if (update.baseVersion === undefined)
        return 'apply';
    if (doc && update.version <= doc.version)
        return 'stale';
    if (doc && doc.version === update.baseVersion)
        return 'apply';
    return 'gap';
}
/**
 * The viewer's side of the live-transcript document protocol, without React.
 *
 * It tracks the held document and whether a `subscribe` is still waiting for
 * its answer. An update that does not fit the held document (`gap`) asks for
 * one snapshot — unless a subscribe is already in flight, whose answer will
 * replace the document anyway (the server can deliver a delta to a fresh
 * subscriber just before its initial snapshot). Updates the viewer already has
 * (`stale`) are dropped. MU N3c, 7. 10. 2026.
 */
export class TranscriptFeed {
    documentId;
    doc;
    awaitingSnapshot = false;
    current = false;
    /**
     * @param initial the document the viewer already shows for this
     * documentId (a re-created feed), so an update arriving before the next
     * snapshot applies to it instead of seeding a fragment.
     */
    constructor(documentId, initial = null) {
        this.documentId = documentId;
        this.doc = initial;
    }
    get document() {
        return this.doc;
    }
    /**
     * A `subscribe` is still waiting for its answer, so {@link document} may be
     * older than the server's (it was frozen while the socket was down).
     */
    get isAwaitingSnapshot() {
        return this.awaitingSnapshot;
    }
    /**
     * {@link document} came from a snapshot or a live update since the last
     * `subscribe` — not a copy frozen while the socket was down, which the
     * viewer must not show over newer text it already has (MU N3c).
     */
    get isCurrent() {
        return this.current;
    }
    /** Call when sending `subscribe` (first connect, reconnect, resync). */
    subscribed() {
        this.awaitingSnapshot = true;
        this.current = false;
    }
    /** Call when the socket closes: the held document stops being live. */
    disconnected() {
        this.current = false;
    }
    /**
     * Call for every answer to a subscribe that carries no document
     * (`waiting_for_writer`, a bare `writer_connected`, an `error`).
     */
    answered() {
        this.awaitingSnapshot = false;
    }
    /** A full `document_state` (or `writer_connected` with a document). */
    snapshot(document) {
        this.awaitingSnapshot = false;
        this.current = true;
        this.doc = document;
        return document;
    }
    /** A `document_update`. */
    update(update) {
        const action = classifyDocumentUpdate(this.doc, update);
        if (action === 'stale')
            return { document: this.doc, resubscribe: false };
        if (action === 'gap') {
            const resubscribe = !this.awaitingSnapshot;
            if (resubscribe)
                this.awaitingSnapshot = true;
            return { document: this.doc, resubscribe };
        }
        this.doc = reduceDocumentUpdate(this.doc, update, this.documentId);
        this.current = true;
        return { document: this.doc, resubscribe: false };
    }
    /** A `streaming_text` frame (WebSocket writer). */
    streamingText(data) {
        this.doc = reduceStreamingText(this.doc, data, this.documentId);
        this.current = true;
        return this.doc;
    }
}
/**
 * Apply a document update, seeding a fresh document when the viewer hasn't
 * received any state yet (MP-182/186). Never returns null, so text is never
 * dropped just because `document_state` was skipped.
 */
export function reduceDocumentUpdate(prev, update, documentId) {
    return applyUpdate(prev ?? createEmptyDocument(documentId), update);
}
/**
 * Apply a streamed-text frame, seeding a fresh document when none exists yet
 * (MP-182/186). This is the fix for "writer typing, no text": the old hook
 * bailed to null here and discarded every character.
 */
export function reduceStreamingText(prev, data, documentId) {
    return applyStreamingText(prev ?? createEmptyDocument(documentId), data);
}
/**
 * The web writer's editor lines for a document the server already holds.
 *
 * The editor keeps one line per paragraph and edits a paragraph by its id, so
 * a writer that reloads the page must get the SAME ids back: new ids would
 * leave the old paragraphs with every viewer and make Clear miss them.
 */
export function writerLinesFromDocument(doc) {
    if (!doc)
        return [];
    return doc.paragraphs.map((paragraph) => ({
        id: paragraph.id,
        text: paragraph.segments.map((segment) => segment.text).join(''),
    }));
}
//# sourceMappingURL=transcript-document.js.map